import React from 'react';
import { Title, Group, Text, Loader, Badge } from '@mantine/core';
import { PageContainer } from '../../components/ui/PageContainer';
import { LoadFailed } from '../../components/ui/LoadFailed';
import { useTranslation } from 'react-i18next';
import { CompetencyDrawer } from '../../components/competency/CompetencyDrawer';
import { ReviewRequestsTable } from '../../components/review/ReviewRequestsTable';
import { ReviewStatsDashboard } from '../../components/review/ReviewStatsDashboard';
import { AssignReviewersModal } from '../../components/review/AssignReviewersModal';
import { CompetencyReviewDetailsModal } from '../../components/competency/CompetencyReviewDetailsModal';
import { useReviewRequestsManager } from '../../hooks/useReviewRequestsManager';

export const CompetencyReviews: React.FC = () => {
  const { t } = useTranslation();

  // Используем кастомный хук для управления review requests
  const {
    reviewRequests,
    isLoading,
    error,
    selectedRequest,
    isModalOpen,
    drawer,
    getStatusConfig,
    handleStatusUpdate,
    handleReject,
    handleRemind,
    handleDelete,
    handleView,
    handleCloseModal,
    handleOpenDrawer,
    handleCloseDrawer,
    assignRequest,
    isSavingReviewers,
    openAssignReviewers,
    closeAssignReviewers,
    handleSaveReviewers,
  } = useReviewRequestsManager({ isAllRequests: true });

  if (isLoading) {
    return (
      <PageContainer>
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <LoadFailed message={t('errors.loadFailed')} hasRetry />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Group gap="md" align="center" mb={{ base: 'md', sm: 'xl' }}>
        <Title order={2}>{t('competencyReviews.title')}</Title>
        {!!reviewRequests?.length && (
          <Badge color="teal" variant="light">
            {reviewRequests.length} {t('competencyReviews.requestsCount')}
          </Badge>
        )}
      </Group>

      {/* Дашборд метрик */}
      <ReviewStatsDashboard />

      {/* Единая таблица всех заявок на ревью */}
      {reviewRequests && reviewRequests.length > 0 ? (
        <ReviewRequestsTable
          title={t('competencyReviews.allRequests')}
          requests={reviewRequests}
          showApplicant={true}
          showReviewer={true}
          onView={handleView}
          onApprove={(requestId) => handleStatusUpdate(requestId, 'approved')}
          onReject={handleReject}
          onRemind={handleRemind}
          onAssignReviewers={openAssignReviewers}
          onDelete={handleDelete}
          onOpenCompetencyDrawer={handleOpenDrawer}
        />
      ) : (
        <Text ta="center" c="dimmed" py="xl">
          {t('competencyReviews.noRequests')}
        </Text>
      )}

      {/* Модальное окно для просмотра деталей */}
      <CompetencyReviewDetailsModal
        opened={isModalOpen}
        onClose={handleCloseModal}
        selectedRequest={selectedRequest}
        getStatusConfig={getStatusConfig}
      />

      {/* Ручное назначение проверяющих (админ) */}
      <AssignReviewersModal
        opened={!!assignRequest}
        request={assignRequest}
        onClose={closeAssignReviewers}
        onSave={handleSaveReviewers}
        isSaving={isSavingReviewers}
      />

      {/* Дровер компетенции */}
      <CompetencyDrawer
        drawer={drawer}
        onClose={handleCloseDrawer}
        // Карта заявки не загружается — грейд неизвестен, не подписываем.
        grades={[]}
        // Без currentUser: карточка здесь справочная. С ним дровер считал бы целью
        // самого смотрящего и предлагал админу «Выдать» компетенцию себе, а не заявителю.
        userCompetencies={[]}
        competencyMapId="default"
        hideStatusAndActions={true}
      />
    </PageContainer>
  );
};
